/**
 * One day of the week board (`Week.dc.html`, canvas PR #60): a row per
 * vehicle with its trips and their stops, and a row per own driver with the
 * duty from the pre-trip check to the last return, against the aim and the
 * limit. Every day of the week is drawn on the same clock axis, so one day
 * reads against the next.
 *
 * A trip is a link to that trip on its day page, so the keyboard reaches each
 * one in drawing order and Enter opens it, as on the day timeline. A duty is
 * text: what the bar shows is written in the row, and the marks for the aim and
 * the limit are drawn for the eye only, because the row says in words when a
 * duty passes either.
 *
 * Positions are percentages set inline, which is layout; every colour and space
 * is a token in `timeline.css`.
 */
import { Link } from "react-router-dom";

import type { Day, Duty, Trip, Week, WeekDay } from "../api";
import { formatClock, formatDate, minutesIntoDay } from "../time";
import { tripLabels, vehicleRows } from "../trips";
import { hourLabel, percent, spanOf, tripName } from "./Timeline";

/** The week board's axis runs 06:00 to midnight; the day timeline's 04:00 to
 *  22:00. The wider of the two, widened again by anything outside it. */
const DEFAULT_START_H = 4;
const DEFAULT_END_H = 24;

export interface Axis {
  startH: number;
  endH: number;
}

/** A week day as the day timeline's helpers take a day: same trips, zone and
 *  date, no map. */
export function asDay(week: Week, day: WeekDay): Day {
  return {
    run_id: week.run_id,
    date: day.date,
    time_zone: week.time_zone,
    depot: null,
    legs_available: false,
    trips: day.trips,
  };
}

function dutySpan(duty: Duty, zone: string, date: string): [number, number] {
  const start = minutesIntoDay(duty.start, zone, date);
  return [start, Math.max(minutesIntoDay(duty.end, zone, date), start)];
}

/** One axis for the whole week: the default hours, widened to every trip and
 *  duty of every day. */
export function weekAxis(week: Week): Axis {
  const spans: [number, number][] = [];
  for (const day of week.days) {
    const asOne = asDay(week, day);
    for (const trip of day.trips) {
      const span = spanOf(trip, asOne);
      if (span) spans.push([span.start, span.end]);
    }
    for (const duty of day.duties) spans.push(dutySpan(duty, week.time_zone, day.date));
  }
  return {
    startH: Math.min(DEFAULT_START_H, ...spans.map(([s]) => Math.floor(s / 60))),
    endH: Math.max(DEFAULT_END_H, ...spans.map(([, e]) => Math.ceil(e / 60))),
  };
}

/** A duty bar at least this share of the axis holds its own sentence. */
const LONG_BAR_PCT = 45;

/** The duty in words, the same sentence for every reader. */
export function dutyText(duty: Duty, week: Week, date: string): string {
  const from = formatClock(duty.start, week.time_zone, date);
  const to = formatClock(duty.end, week.time_zone, date);
  const base = `${duty.hours.toFixed(1)} h duty, ${from} to ${to}`;
  if (duty.status === "above_limit") return `${base}, above the ${week.duty_limit_hours} h limit`;
  if (duty.status === "above_aim") return `${base}, above the ${week.duty_aim_hours} h aim`;
  return base;
}

export function WeekTimeline({ week, day, axis }: { week: Week; day: WeekDay; axis: Axis }) {
  const asOne = asDay(week, day);
  const rows = vehicleRows(day.trips);
  const labels = tripLabels(day.trips);
  const zone = week.time_zone;
  const axisStart = axis.startH * 60;
  const axisLength = (axis.endH - axis.startH) * 60;
  const at = (minute: number) => ((minute - axisStart) / axisLength) * 100;
  const width = (minutes: number) => (minutes / axisLength) * 100;
  const step = axis.endH - axis.startH > 12 ? 2 : 1;
  const ticks: number[] = [];
  for (let h = axis.startH; h <= axis.endH; h += step) ticks.push(h);
  const dayPath = `/runs/${week.run_id}/days/${day.date}`;
  // Seven of these on one page: each landmark and list says which day it is.
  const named = formatDate(day.date);

  return (
    // Wide on purpose; it scrolls in here at a phone's width, never the page
    // (WCAG 1.4.10). Every trip in it is a link, so the keyboard reaches it.
    <div
      aria-label={`Timeline for ${named}, scrolls sideways on a narrow screen`}
      className="timeline__scroll"
      role="region"
    >
      <div className="timeline timeline--week">
        <div aria-hidden="true" className="timeline__axis">
          {ticks.map((h) => (
            <span className="timeline__tick" key={h} style={{ left: percent(at(h * 60)) }}>
              {hourLabel(h)}
            </span>
          ))}
        </div>
        <ul aria-label={`Vehicles and their trips on ${named}`} className="timeline__rows" role="list">
          {rows.map((row) => (
            <li className="timeline__row" key={row.key}>
              <span className="timeline__vehicle">
                {row.name}
                {row.vehicle.external ? <span className="timeline__note"> haulier</span> : null}
              </span>
              <div className="timeline__track">
                {row.trips.map((trip) => {
                  const span = spanOf(trip, asOne);
                  if (!span) return null;
                  const label = labels.get(trip.route_id) ?? "Trip";
                  const length = Math.max(span.end - span.start, 1);
                  return (
                    <Link
                      aria-label={tripName(trip, label, row.name, asOne)}
                      className="timeline__trip"
                      key={trip.route_id}
                      style={{ left: percent(at(span.start)), width: percent(width(length)) }}
                      to={`${dayPath}?trip=${trip.route_id}`}
                    >
                      <span className="timeline__label">{label}</span>
                      {trip.stops.map((stop) => (
                        <span
                          aria-hidden="true"
                          className="timeline__stop"
                          key={stop.stop_seq}
                          style={{
                            left: percent(
                              ((minutesIntoDay(stop.arrival, zone, day.date) - span.start) / length) *
                                100,
                            ),
                          }}
                        />
                      ))}
                      {trip.return ? <span aria-hidden="true" className="timeline__return" /> : null}
                    </Link>
                  );
                })}
              </div>
            </li>
          ))}
        </ul>
        {day.duties.length > 0 ? (
          <ul aria-label={`Driver duties on ${named}`} className="timeline__rows" role="list">
            {day.duties.map((duty) => {
              const [start, end] = dutySpan(duty, zone, day.date);
              const text = dutyText(duty, week, day.date);
              const left = at(start);
              const right = at(end);
              // The sentence goes on a bar long enough to hold it, and beside a
              // shorter one, on the side with more room, as the board writes it.
              const place =
                right - left >= LONG_BAR_PCT ? "inside" : 100 - right >= left ? "after" : "before";
              const marks = [
                ["timeline__limit timeline__limit--aim", at(start + week.duty_aim_hours * 60)],
                ["timeline__limit", at(start + week.duty_limit_hours * 60)],
              ] as const;
              const tone = duty.status === "within" ? "" : ` timeline__duty--${duty.status.replace("_", "-")}`;
              return (
                <li className="timeline__row" key={duty.driver_slot}>
                  <span className="timeline__vehicle">
                    Driver {duty.driver_slot}
                    <span className="visually-hidden">: </span>
                  </span>
                  <div className="timeline__track timeline__track--duty">
                    <span
                      aria-hidden="true"
                      className={`timeline__duty${tone}`}
                      style={{ left: percent(left), width: percent(Math.max(right - left, 0.1)) }}
                    />
                    <span
                      className={`timeline__duty-text timeline__duty-text--${place}${tone ? " timeline__duty-text--flagged" : ""}`}
                      style={
                        place === "before"
                          ? { right: percent(100 - left) }
                          : { left: percent(place === "after" ? right : left) }
                      }
                    >
                      {text}
                    </span>
                    {/* Only the marks that fall on the axis: a late duty's aim
                        lies in the small hours, past the day drawn here. */}
                    {marks
                      .filter(([, x]) => x >= 0 && x <= 100)
                      .map(([className, x]) => (
                        <span aria-hidden="true" className={className} key={className} style={{ left: percent(x) }} />
                      ))}
                  </div>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
    </div>
  );
}
